<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header (matching create page) -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Doctor Speciality</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify doctor specialization assignment and details
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                      {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
                    </v-btn>
                    
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Specialities
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Doctor Speciality Information</h2>
              <v-spacer></v-spacer>
              <v-chip 
                :color="isEditMode ? 'success' : 'warning'" 
                :outlined="!isEditMode" 
                x-small
              >
                <v-icon left x-small>
                  {{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}
                </v-icon>
                {{ isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            
            <!-- Loading State -->
            <div v-if="pageLoading" class="text-center py-12">
              <health-loader message="Loading doctor speciality..." />
            </div>

            <!-- Form Content -->
            <v-card-text v-else class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Basic Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-medical-bag</v-icon>
                    Doctor Speciality Details
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.doctor_id"
                        :items="doctors"
                        :loading="loadingDoctors"
                        item-text="text"
                        item-value="value"
                        label="Doctor"
                        placeholder="Select a doctor"
                        solo
                        rounded
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Doctor is required']"
                        :error-messages="errors.doctor_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                        @change="checkDuplicateCombination"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-doctor</v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-avatar size="30">
                            <v-icon color="primary">mdi-doctor</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                            <v-list-item-subtitle v-if="item.email">
                              {{ item.email }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.specialization_id"
                        :items="specializations"
                        :loading="loadingSpecializations"
                        item-text="text"
                        item-value="value"
                        label="Specialization"
                        placeholder="Select a specialization"
                        solo
                        rounded
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Specialization is required']"
                        :error-messages="errors.specialization_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                        @change="checkDuplicateCombination"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-medical-bag</v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-avatar size="30">
                            <v-icon color="success">mdi-medical-bag</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                            <v-list-item-subtitle v-if="item.description">
                              {{ item.description }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <!-- Duplicate Warning Alert -->
                  <v-row v-if="duplicateWarning && isEditMode">
                    <v-col cols="12">
                      <v-alert
                        type="warning"
                        outlined
                        dense
                        class="mb-2"
                      >
                        <v-icon slot="prepend">mdi-alert</v-icon>
                        This doctor already has this specialization assigned.
                      </v-alert>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-switch
                        v-model="form.is_active"
                        label="Active Status"
                        color="success"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :false-value="false"
                        :true-value="true"
                        hide-details
                        class="professional-field mt-0 pt-0"
                      >
                        <template v-slot:prepend>
                          <v-icon color="grey">mdi-toggle-switch</v-icon>
                        </template>
                      </v-switch>
                      <p class="text-caption grey--text mt-1 mb-0">
                        {{ form.is_active ? 'Speciality is active and visible' : 'Speciality is inactive and hidden' }}
                      </p>
                    </v-col>
                  </v-row>
                </div>

                <!-- Metadata Section -->
                <div class="form-section mb-2" v-if="doctorSpeciality">
                  <h3 class="section-title mb-1">
                    <v-icon color="info" class="mr-2">mdi-information</v-icon>
                    Record Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="doctorSpeciality.created_at | formatDate"
                        label="Created Date"
                        solo
                        rounded
                        readonly
                        disabled
                        class="professional-field"
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="doctorSpeciality.created_by ? `${doctorSpeciality.created_by.first_name} ${doctorSpeciality.created_by.last_name}` : 'System'"
                        label="Created By"
                        solo
                        rounded
                        readonly
                        disabled
                        class="professional-field"
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="doctorSpeciality.updated_at | formatDate"
                        label="Last Updated"
                        solo
                        rounded
                        readonly
                        disabled
                        class="professional-field"
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-update</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="doctorSpeciality.company ? doctorSpeciality.company.name : 'N/A'"
                        label="Company"
                        solo
                        rounded
                        readonly
                        disabled
                        class="professional-field"
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-domain</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <!-- Form Actions (only show in edit mode) -->
            <div v-if="isEditMode && !pageLoading">
              <v-divider></v-divider>
              <v-card-actions class="form-actions px-3 py-2">
                <v-row class="w-100 ma-0" align="center">
                  <!-- Left side: Form validation status -->
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex align-center">
                      <v-chip
                        :color="valid ? 'success' : 'warning'"
                        outlined
                        x-small
                        class="mr-2"
                      >
                        <v-icon left x-small>
                          {{ valid ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        {{ valid ? 'Form Valid' : 'Check Required Fields' }}
                      </v-chip>
                      <span class="text-caption grey--text">
                        Please fill all required fields before saving
                      </span>
                    </div>
                  </v-col>

                  <!-- Right side: Action buttons -->
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex align-center justify-end">
                      <v-btn
                        color="error"
                        outlined
                        @click="confirmDelete"
                        class="mr-2"
                        :disabled="loading"
                      >
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                      </v-btn>

                      <v-btn
                        color="grey darken-1"
                        outlined
                        @click="resetForm"
                        class="mr-2"
                        :disabled="loading"
                      >
                        <v-icon left>mdi-refresh</v-icon>
                        Reset
                      </v-btn>

                      <v-btn
                        color="primary"
                        :disabled="!valid || duplicateWarning"
                        :loading="loading"
                        @click="update"
                        class="save-button"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Update Speciality
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-actions>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "EditDoctorSpeciality",
  data() {
    return {
      valid: false,
      loading: false,
      pageLoading: true,
      loadingDoctors: false,
      loadingSpecializations: false,
      isEditMode: false,
      duplicateWarning: false,
      doctorSpeciality: null,
      originalForm: {},
      form: {
        doctor_id: null,
        specialization_id: null,
        is_active: true,
      },
      doctors: [],
      specializations: [],
      errors: {},
    };
  },
  filters: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    },
  },
  created() {
    this.loadDoctorSpeciality();
    this.getDoctors();
    this.getSpecializations();
  },
  methods: {
    async loadDoctorSpeciality() {
      this.pageLoading = true;
      try {
        const id = this.$route.params.id;
        const response = await axios.get(`doctor_specialities/${id}`);
        
        this.doctorSpeciality = response.data.data;
        this.form = {
          doctor_id: this.doctorSpeciality.doctor_id,
          specialization_id: this.doctorSpeciality.specialization_id,
          is_active: this.doctorSpeciality.is_active,
        };
        this.originalForm = { ...this.form };
      } catch (error) {
        console.error("Error loading doctor speciality:", error);
        this.$toast.error("Failed to load doctor speciality");
        this.$router.push("/doctor_specialities");
      } finally {
        this.pageLoading = false;
      }
    },

    async getDoctors() {
      this.loadingDoctors = true;
      try {
        const response = await axios.get("users", {
          params: { is_active: 1, rowsPerPage: 1000 },
        });
        this.doctors = response.data.data.map((doctor) => ({
          text: `${doctor.first_name} ${doctor.last_name}`,
          value: doctor.id,
          email: doctor.email,
        }));
      } catch (error) {
        console.error("Error fetching doctors:", error);
        this.$toast.error("Failed to load doctors");
      } finally {
        this.loadingDoctors = false;
      }
    },

    async getSpecializations() {
      this.loadingSpecializations = true;
      try {
        const response = await axios.get("value_lists", {
          params: { category: "medical_specialization", rowsPerPage: 1000 },
        });
        this.specializations = response.data.data.map((specialization) => ({
          text: specialization.name,
          value: specialization.id,
          description: specialization.description,
        }));
      } catch (error) {
        console.error("Error fetching specializations:", error);
        this.$toast.error("Failed to load specializations");
      } finally {
        this.loadingSpecializations = false;
      }
    },

    async checkDuplicateCombination() {
      if (this.form.doctor_id && this.form.specialization_id) {
        // Don't warn if it's the same as original combination
        if (
          this.form.doctor_id === this.originalForm.doctor_id &&
          this.form.specialization_id === this.originalForm.specialization_id
        ) {
          this.duplicateWarning = false;
          return;
        }

        try {
          const response = await axios.get("doctor_specialities", {
            params: {
              doctor_id: this.form.doctor_id,
              specialization_id: this.form.specialization_id,
              rowsPerPage: 1,
            },
          });
          this.duplicateWarning = response.data.count > 0;
        } catch (error) {
          console.error("Error checking duplicate:", error);
          this.duplicateWarning = false;
        }
      } else {
        this.duplicateWarning = false;
      }
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      if (!this.isEditMode) {
        this.resetForm();
      }
    },

    async update() {
      if (!this.$refs.form.validate()) {
        this.$toast.error("Please fill all required fields");
        return;
      }

      if (this.duplicateWarning) {
        this.$toast.error("This doctor-specialization combination already exists");
        return;
      }

      this.loading = true;
      this.errors = {};

      try {
        const id = this.$route.params.id;
        const response = await axios.patch(`doctor_specialities/${id}`, this.form);

        if (response.data.success) {
          this.$toast.success("Doctor speciality updated successfully");
          this.originalForm = { ...this.form };
          this.loadDoctorSpeciality(); // Reload to get updated data
          this.isEditMode = false;
        }
      } catch (error) {
        console.error("Error updating doctor speciality:", error);
        
        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
          this.$toast.error("Please check the form data");
        } else {
          this.$toast.error("Failed to update doctor speciality");
        }
      } finally {
        this.loading = false;
      }
    },

    async confirmDelete() {
      if (await this.$confirm("Are you sure you want to delete this doctor speciality?")) {
        await this.deleteDoctorSpeciality();
      }
    },

    async deleteDoctorSpeciality() {
      this.loading = true;
      try {
        const id = this.$route.params.id;
        await axios.post(`doctor_specialities/delete/${id}`);
        
        this.$toast.success("Doctor speciality deleted successfully");
        this.$router.push("/doctor_specialities");
      } catch (error) {
        console.error("Error deleting doctor speciality:", error);
        this.$toast.error("Failed to delete doctor speciality");
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = { ...this.originalForm };
      this.errors = {};
      this.duplicateWarning = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
/* Page Header Card */
.page-header-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.page-subtitle {
  font-size: 0.875rem;
  line-height: 1.4;
  margin-top: 2px;
}

/* Form Card Styling */
.form-card {
  max-width: 1200px;
  margin: 0 auto;
}

/* Form Header */
.form-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

/* Form Content Styling */
.form-content {
  background-color: #fafafa;
}

.form-section {
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

/* Professional Field Styling */
.professional-field {
  margin-bottom: 0.5rem;
}

.professional-field >>> .v-input__control {
  min-height: 48px;
}

.professional-field >>> .v-text-field__details {
  margin-top: 4px;
  padding-left: 12px;
}

/* Disabled/Readonly Field Styling */
.professional-field.v-input--is-disabled >>> .v-input__control {
  opacity: 0.7;
}

.professional-field.v-input--is-readonly >>> .v-input__control {
  background-color: #f5f5f5;
}

/* Form Actions */
.form-actions {
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.save-button {
  min-width: 160px;
  font-weight: 600;
}

.back-button {
  min-width: 140px;
}

/* Mode Toggle Styles */
.v-btn.warning {
  background-color: #ff9800 !important;
  color: white !important;
}

.v-btn.primary {
  background-color: #1976d2 !important;
  color: white !important;
}

/* Success/Error State Colors */
.v-chip.success {
  background-color: #e8f5e8 !important;
  color: #2e7d32 !important;
}

.v-chip.warning {
  background-color: #fff3e0 !important;
  color: #f57c00 !important;
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.25rem;
  }
  
  .form-section {
    padding: 1rem;
  }
  
  .form-actions .v-col {
    text-align: center !important;
  }
  
  .form-actions .d-flex {
    justify-content: center !important;
  }
}

/* Focus States */
.professional-field >>> .v-input--is-focused .v-input__control {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Loading States */
.v-btn--loading {
  pointer-events: none;
}

/* Animation for form sections */
.form-section {
  animation: slideInUp 0.3s ease-out;
}

@keyframes slideInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>